import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Save, ExternalLink, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/pages/admin/api";

const PLACEMENT_HINT = {
  header: "Shows just below the site header on every public page.",
  in_content: "Available inside content sections (use <AdSlot placement=\"in_content\" />).",
  sidebar: "Reserved for sidebar/rail placements.",
  footer: "Shows above the site footer on every public page.",
};

export default function AdsenseSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/ads").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });
  const setSlot = (id, k, v) =>
    setData({ ...data, slots: data.slots.map((s) => (s.id === id ? { ...s, [k]: v } : s)) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/ads", data);
      setData(r.data);
      toast.success("AdSense settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const saveBtn = (
    <Button data-testid="save-adsense" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
      <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Changes"}
    </Button>
  );

  return (
    <div className="space-y-6">
      <Panel
        title="Google AdSense"
        subtitle="Paste your AdSense code here — it is injected into every public page automatically."
        actions={saveBtn}
      >
        <div className="flex items-center justify-between rounded-xl border border-border p-4 mb-5">
          <div>
            <div className="text-sm font-medium flex items-center gap-2"><DollarSign size={14} /> Enable AdSense</div>
            <p className="text-xs text-muted-foreground mt-0.5">Master switch. When off, no ad code is loaded anywhere.</p>
          </div>
          <Switch data-testid="ads-enabled-toggle" checked={!!data.enabled} onCheckedChange={(v) => set("enabled", v)} />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Publisher ID" hint="Format: ca-pub-1234567890123456">
            <Input data-testid="ads-publisher-id" value={data.publisher_id || ""} onChange={(e) => set("publisher_id", e.target.value)} placeholder="ca-pub-XXXXXXXXXXXXXXXX" />
          </Field>
          <div className="flex items-center justify-between rounded-xl border border-border px-4">
            <div>
              <div className="text-sm font-medium">Auto Ads</div>
              <p className="text-xs text-muted-foreground">Let Google place ads automatically.</p>
            </div>
            <Switch data-testid="ads-auto-toggle" checked={!!data.auto_ads} onCheckedChange={(v) => set("auto_ads", v)} />
          </div>
        </div>

        <Field label="Custom Head Code" className="mt-4" hint="Any extra AdSense / ad-network script to inject in <head>.">
          <Textarea data-testid="ads-head-code" rows={5} className="font-mono text-xs" value={data.head_code || ""} onChange={(e) => set("head_code", e.target.value)} placeholder={'<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXX" crossorigin="anonymous"></script>'} />
        </Field>
      </Panel>

      <Panel title="Ad Slots" subtitle="Paste the ad unit code Google gives you for each position." actions={saveBtn}>
        <div className="space-y-4">
          {(data.slots || []).map((s) => (
            <div key={s.id} className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium">{s.name}</div>
                  <p className="text-xs text-muted-foreground">{PLACEMENT_HINT[s.placement]}</p>
                </div>
                <Switch data-testid={`ads-slot-toggle-${s.id}`} checked={!!s.enabled} onCheckedChange={(v) => setSlot(s.id, "enabled", v)} />
              </div>
              <Textarea
                data-testid={`ads-slot-code-${s.id}`}
                rows={4}
                className="font-mono text-xs"
                placeholder='<ins class="adsbygoogle" style="display:block" data-ad-client="ca-pub-XXXX" data-ad-slot="1234567890"></ins>'
                value={s.code || ""}
                onChange={(e) => setSlot(s.id, "code", e.target.value)}
              />
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="ads.txt" subtitle="Required by Google to verify you own the ad inventory." actions={saveBtn}>
        <Field label="ads.txt content" hint="Leave empty to auto-generate from your Publisher ID.">
          <Textarea data-testid="ads-txt" rows={4} className="font-mono text-xs" value={data.ads_txt || ""} onChange={(e) => set("ads_txt", e.target.value)} placeholder="google.com, pub-XXXXXXXXXXXXXXXX, DIRECT, f08c47fec0942fa0" />
        </Field>
        <div className="flex flex-wrap gap-2 mt-4">
          <a data-testid="view-ads-txt" href={`${API}/seo/ads.txt`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-xl"><ExternalLink size={14} className="mr-1" /> View live ads.txt</Button>
          </a>
        </div>
      </Panel>
    </div>
  );
}
